// Lightweight i18n for the AlveeFx Clip Program.
// Scope: Clip Landing Page (/clip) + Clipper Dashboard (/clip/dashboard).
// Numbers/currency stay in English digits (financial UX convention).
// Bangla is written in native Bangla script (আয় করুন...).

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from './api';

const LANG_KEY = 'alvee_clip_lang';           // user's manual override
const AUTO_DONE_KEY = 'alvee_clip_lang_auto'; // set once auto-detection ran

// ---------------------------------------------------------------------------
// Translation dictionary. Keys are ENGLISH strings so untranslated fallbacks
// still render meaningfully. Add new keys here as needed.
// ---------------------------------------------------------------------------
const TRANSLATIONS = {
  // ---------- Landing page ----------
  'AlveeFx Clip Program': 'AlveeFx ক্লিপ প্রোগ্রাম',
  'Earn $40 per 1M views': 'প্রতি ১ মিলিয়ন ভিউতে আয় করুন $40',
  'Earn $40 per 1M views — Clip AlveeFx Content':
    'AlveeFx কন্টেন্ট ক্লিপ করে প্রতি ১ মিলিয়ন ভিউতে আয় করুন $40',
  'Repost AlveeFx clips on TikTok, Instagram Reels and Facebook. Get paid in USDT BEP-20 for every view.':
    'AlveeFx ক্লিপগুলো TikTok, Instagram Reels ও Facebook-এ রিপোস্ট করুন। প্রতিটি ভিউয়ের জন্য USDT BEP-20 তে পেমেন্ট নিন।',
  'Start Clipping': 'ক্লিপিং শুরু করুন',
  'Sign in': 'সাইন ইন',
  'Sign up': 'সাইন আপ',
  'Login': 'লগইন',
  'Logout': 'লগআউট',
  'Dashboard': 'ড্যাশবোর্ড',
  'How it works': 'কীভাবে কাজ করে',
  'Get started in 3 steps': '৩ ধাপে শুরু করুন',
  'Sign up as clipper': 'ক্লিপার হিসেবে সাইন আপ করুন',
  'Create a free account and register your TikTok, Instagram & Facebook handles.':
    'ফ্রি অ্যাকাউন্ট খুলুন এবং আপনার TikTok, Instagram ও Facebook হ্যান্ডেল রেজিস্টার করুন।',
  'Repost & submit': 'রিপোস্ট করুন এবং সাবমিট করুন',
  'Download AlveeFx source clips from our Telegram channel. Post them with the required attribution and submit the URL.':
    'আমাদের Telegram চ্যানেল থেকে AlveeFx সোর্স ক্লিপ ডাউনলোড করুন। প্রয়োজনীয় অ্যাট্রিবিউশন সহ পোস্ট করে URL সাবমিট করুন।',
  'Get paid': 'পেমেন্ট পান',
  'We track views 24/7 and pay $40 per 1,000,000 views in USDT BEP-20.':
    'আমরা ২৪/৭ ভিউ ট্র্যাক করি এবং প্রতি ১,০০০,০০০ ভিউতে $40 USDT BEP-20 তে পেমেন্ট দিই।',
  'Why AlveeFx Clip Program?': 'কেন AlveeFx ক্লিপ প্রোগ্রাম?',
  'No follower requirement': 'কোনো ফলোয়ার লাগবে না',
  'Anyone can start — even brand new accounts.':
    'যেকেউ শুরু করতে পারেন — একদম নতুন অ্যাকাউন্ট হলেও।',
  'Fast crypto payouts': 'দ্রুত ক্রিপ্টো পেমেন্ট',
  'Payouts in USDT BEP-20. $10 minimum. Weekly cycles.':
    'USDT BEP-20 তে পেমেন্ট। মিনিমাম $10। সাপ্তাহিক পেমেন্ট সাইকেল।',
  'Real-time tracking': 'রিয়েল-টাইম ট্র্যাকিং',
  'View counts scraped every few hours. See your earnings grow live.':
    'প্রতি কয়েক ঘণ্টায় ভিউ কাউন্ট আপডেট হয়। লাইভ আপনার আয় বাড়তে দেখুন।',
  'Rate': 'রেট',
  'per 1M views': 'প্রতি ১ মিলিয়ন ভিউ',
  'Minimum payout': 'মিনিমাম পেআউট',
  'Payment': 'পেমেন্ট',

  // ---------- Landing page — extended ----------
  'Clip Program': 'ক্লিপ প্রোগ্রাম',
  'Log in': 'লগইন',
  'Start clipping': 'ক্লিপিং শুরু',
  'Open · Instant Approval': 'ওপেন · ইনস্ট্যান্ট অ্যাপ্রুভাল',
  'Make short clips from official AlveeFx videos, post them on TikTok / Instagram / Facebook, and get paid in USDT. We track your views automatically every few hours.':
    'অফিসিয়াল AlveeFx ভিডিও থেকে শর্ট ক্লিপ বানান, TikTok / Instagram / Facebook-এ পোস্ট করুন, এবং USDT-তে পেমেন্ট নিন। প্রতি কয়েক ঘণ্টায় আপনার ভিউ আমরা অটোমেটিক ট্র্যাক করি।',
  'Open Dashboard': 'ড্যাশবোর্ড খুলুন',
  'Start clipping for free': 'ফ্রি ক্লিপিং শুরু করুন',
  'Get source videos on Telegram': 'Telegram থেকে সোর্স ভিডিও নিন',
  'Program currently paused — signups disabled': 'প্রোগ্রাম আপাতত বন্ধ — সাইনআপ ডিসেবল',
  'Min withdrawal': 'মিনিমাম উইথড্র',
  'Total views tracked': 'ট্র্যাক করা মোট ভিউ',
  'Active clippers': 'অ্যাক্টিভ ক্লিপার',
  'Get source videos': 'সোর্স ভিডিও পান',
  'Join our Telegram channel for the latest AlveeFx clips, raw footage, and brand assets. All free to remix.':
    'সর্বশেষ AlveeFx ক্লিপ, র ফুটেজ ও ব্র্যান্ড অ্যাসেটের জন্য আমাদের Telegram চ্যানেলে জয়েন করুন। সব ফ্রি রিমিক্স করার জন্য।',
  'Edit & post': 'এডিট ও পোস্ট',
  'Cut a short clip (15–60s recommended), add hooks and captions, post to TikTok, Instagram Reels, or Facebook Reels.':
    'একটি শর্ট ক্লিপ কাটুন (১৫–৬০ সেকেন্ড রেকমেন্ডেড), হুক ও ক্যাপশন যোগ করুন, TikTok, Instagram Reels বা Facebook Reels-এ পোস্ট করুন।',
  'Submit your URL': 'আপনার URL সাবমিট করুন',
  'On your dashboard, paste the URL of your post. We automatically scan view counts every few hours.':
    'আপনার ড্যাশবোর্ডে পোস্টের URL পেস্ট করুন। আমরা প্রতি কয়েক ঘণ্টায় ভিউ কাউন্ট অটোমেটিক স্ক্যান করি।',
  'Withdraw in USDT': 'USDT তে উইথড্র',
  'See it in action': 'উদাহরণ দেখুন',
  'Example AlveeFx clips': 'AlveeFx ক্লিপের উদাহরণ',
  'Quick reference of the kind of edits that perform well. Tap to play in-place.':
    'ভালো পারফর্ম করে এমন এডিটের কুইক রেফারেন্স। ট্যাপ করে এখানেই প্লে করুন।',
  'Open & transparent payouts': 'ওপেন ও ট্রান্সপারেন্ট পেআউট',
  'Views refresh every ~6 hours': 'প্রতি ~৬ ঘণ্টায় ভিউ রিফ্রেশ',
  'USDT BEP-20 withdrawals': 'USDT BEP-20 উইথড্র',
  'Which platforms count?': 'কোন প্ল্যাটফর্ম কাউন্ট হয়?',
  'TikTok, Instagram Reels, and Facebook Reels. All public posts only. Views from private accounts or shadow-banned posts will not be tracked.':
    'TikTok, Instagram Reels ও Facebook Reels। শুধু পাবলিক পোস্ট। প্রাইভেট অ্যাকাউন্ট বা shadow-banned পোস্টের ভিউ ট্র্যাক করা হবে না।',
  'How fast do views update?': 'ভিউ কত দ্রুত আপডেট হয়?',
  'Every ~6 hours we re-check all your live clips. You can also manually refresh on your dashboard.':
    'প্রতি ~৬ ঘণ্টায় আমরা আপনার সব লাইভ ক্লিপ রি-চেক করি। ড্যাশবোর্ড থেকে ম্যানুয়ালি রিফ্রেশও করতে পারবেন।',
  'Can someone steal my clip URL?': 'কেউ কি আমার ক্লিপ URL চুরি করতে পারবে?',
  'No — each URL can only be submitted by one clipper. First come, first served. Duplicates are rejected.':
    'না — প্রতিটি URL শুধু একজন ক্লিপারই সাবমিট করতে পারবে। ফার্স্ট কাম, ফার্স্ট সার্ভড। ডুপ্লিকেট রিজেক্ট হবে।',
  'When is the first payout?': 'প্রথম পেআউট কখন হবে?',
  'Ready to start earning?': 'আয় শুরু করতে প্রস্তুত?',
  "Sign up takes 30 seconds — no admin approval, no waiting. Plug in your USDT wallet and you're live.":
    'সাইনআপে মাত্র ৩০ সেকেন্ড — কোনো অ্যাডমিন অ্যাপ্রুভাল লাগবে না, অপেক্ষা নেই। USDT ওয়ালেট প্লাগ করে লাইভ হয়ে যান।',
  'Create my clipper account': 'আমার ক্লিপার অ্যাকাউন্ট তৈরি করুন',
  'AlveeFx Clip Program · Earnings vary with platform performance · We reserve the right to remove clips that violate platform ToS or our content guidelines.':
    'AlveeFx ক্লিপ প্রোগ্রাম · আয় প্ল্যাটফর্মের পারফরম্যান্স অনুসারে পরিবর্তিত হয় · প্ল্যাটফর্ম ToS বা আমাদের কন্টেন্ট গাইডলাইন লঙ্ঘন করলে ক্লিপ সরানোর অধিকার আমাদের।',

  // ---------- Dashboard extras ----------
  'Verifying your clip': 'আপনার ক্লিপ ভেরিফাই হচ্ছে',
  'Copy address': 'ঠিকানা কপি',
  'Update wallet': 'ওয়ালেট আপডেট',
  'How much do you want to withdraw?': 'কত টাকা উইথড্র করতে চান?',
  'Available balance:': 'উপলব্ধ ব্যালেন্স:',
  'Enter amount': 'পরিমাণ দিন',
  'Please set your USDT wallet in Profile first.': 'প্রথমে Profile-এ আপনার USDT ওয়ালেট সেট করুন।',
  'Loading...': 'লোড হচ্ছে...',
  'Something went wrong': 'কিছু ভুল হয়েছে',

  // ---------- Auth ----------
  'Email': 'ইমেইল',
  'Password': 'পাসওয়ার্ড',
  'Full name': 'পুরো নাম',
  'Country': 'দেশ',
  'Telegram handle': 'Telegram হ্যান্ডেল',
  'Country (optional)': 'দেশ (ঐচ্ছিক)',
  'Telegram handle (optional)': 'Telegram হ্যান্ডেল (ঐচ্ছিক)',
  'Already have an account?': 'আগে থেকে অ্যাকাউন্ট আছে?',
  "Don't have an account?": 'অ্যাকাউন্ট নেই?',
  'Create account': 'অ্যাকাউন্ট তৈরি করুন',
  'Welcome back': 'আবার স্বাগতম',
  'Social handles (recommended)': 'সোশ্যাল হ্যান্ডেল (রেকমেন্ডেড)',
  "Add the accounts you'll post clips from. Required before submitting your first clip. You can also add them later from your Dashboard. Multiple handles per platform allowed (comma-separated).":
    'যেসব অ্যাকাউন্ট থেকে ক্লিপ পোস্ট করবেন সেগুলো যোগ করুন। প্রথম ক্লিপ সাবমিটের আগে দিতে হবে। পরে ড্যাশবোর্ড থেকেও যোগ করতে পারবেন। প্রতি প্ল্যাটফর্মে একাধিক হ্যান্ডেল দেওয়া যাবে (কমা দিয়ে আলাদা করুন)।',
  'TikTok handles (optional)': 'TikTok হ্যান্ডেল (ঐচ্ছিক)',
  'Instagram handles (optional)': 'Instagram হ্যান্ডেল (ঐচ্ছিক)',
  'Facebook page handles (optional)': 'Facebook পেজ হ্যান্ডেল (ঐচ্ছিক)',

  // ---------- Dashboard ----------
  'Total views': 'মোট ভিউ',
  'Total earned': 'মোট আয়',
  'Pending balance': 'পেন্ডিং ব্যালেন্স',
  'Paid out': 'পেমেন্ট হয়েছে',
  'Available': 'উপলব্ধ',
  'Held': 'হোল্ডে রাখা',
  'Clips': 'ক্লিপস',
  'Withdrawals': 'উইথড্র',
  'Profile': 'প্রোফাইল',
  'Leaderboard': 'লিডারবোর্ড',
  'Top 5 Earners This Week': 'এই সপ্তাহের টপ ৫ আর্নার',
  'Source videos': 'সোর্স ভিডিও',
  'Submit a new clip': 'নতুন ক্লিপ সাবমিট করুন',
  'Submit clip': 'ক্লিপ সাবমিট',
  'Add clip': 'ক্লিপ যোগ করুন',
  'Platform': 'প্ল্যাটফর্ম',
  'Clip URL': 'ক্লিপ URL',
  'Title (optional)': 'টাইটেল (ঐচ্ছিক)',
  'Cancel': 'বাতিল',
  'Before you submit — clip requirements':
    'সাবমিটের আগে — ক্লিপের শর্তসমূহ',
  'Clip must be posted from your own account': 'ক্লিপটি আপনার নিজের অ্যাকাউন্ট থেকে পোস্ট করা থাকতে হবে',
  'Post must be public (not private / age-restricted).':
    'পোস্টটি পাবলিক থাকতে হবে (প্রাইভেট / age-restricted নয়)।',
  'Caption must include at least ONE of:': 'ক্যাপশনে নিচের যেকোনো একটি অবশ্যই থাকতে হবে:',
  'Caption must ALSO include:': 'ক্যাপশনে এগুলোও থাকতে হবে:',
  'Save changes': 'পরিবর্তন সংরক্ষণ করুন',
  'Your profile': 'আপনার প্রোফাইল',
  'Name': 'নাম',
  'USDT BEP-20 address': 'USDT BEP-20 ঠিকানা',
  'Social handles': 'সোশ্যাল হ্যান্ডেল',
  'Add the accounts you post clips from. We use these to verify each submitted clip is actually yours. You can add multiple handles per platform (separate with commas).':
    'যেসব অ্যাকাউন্ট থেকে ক্লিপ পোস্ট করেন সেগুলো যোগ করুন। প্রতিটি সাবমিট করা ক্লিপ আপনার কি না তা যাচাই করতে এগুলো ব্যবহার করা হয়। প্রতি প্ল্যাটফর্মে একাধিক হ্যান্ডেল দেওয়া যাবে (কমা দিয়ে)।',
  'Withdraw earnings': 'আয় উইথড্র করুন',
  'Request withdrawal': 'উইথড্র রিকোয়েস্ট',
  'Amount (USD)': 'পরিমাণ (USD)',
  'Withdrawal history': 'উইথড্র হিস্টোরি',
  'No withdrawals yet': 'এখনো কোনো উইথড্র নেই',
  'No clips yet': 'এখনো কোনো ক্লিপ নেই',
  'Submit your first clip to start earning!': 'আয় শুরু করতে প্রথম ক্লিপটি সাবমিট করুন!',
  'Refresh': 'রিফ্রেশ',
  'Last scan': 'শেষ স্ক্যান',
  'Views': 'ভিউ',
  'Earned': 'আয়',
  'Status': 'স্ট্যাটাস',
  'Verifying your clip — this can take 20–60 seconds…':
    'আপনার ক্লিপ ভেরিফাই করা হচ্ছে — ২০–৬০ সেকেন্ড সময় লাগতে পারে…',
  'Clip verified & submitted ✓': 'ক্লিপ ভেরিফাই ও সাবমিট হয়েছে ✓',
  'Please paste your clip URL': 'অনুগ্রহ করে ক্লিপ URL দিন',
  'Failed to submit': 'সাবমিট ব্যর্থ',
  'Profile updated': 'প্রোফাইল আপডেট হয়েছে',
  'Save failed': 'সংরক্ষণ ব্যর্থ',
  'Earnings on hold — pending review': 'আয় হোল্ডে আছে — রিভিউ পেন্ডিং',
  'on hold — pending review': 'হোল্ডে আছে — রিভিউ পেন্ডিং',
  'Language': 'ভাষা',
  'English': 'English',
  'বাংলা': 'বাংলা',
};

// ---------------------------------------------------------------------------
// Provider + hook
// ---------------------------------------------------------------------------
const I18nContext = createContext({ lang: 'en', setLang: () => {}, t: (s) => s });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      return localStorage.getItem(LANG_KEY) || 'en';
    } catch { return 'en'; }
  });

  // Auto-detect on first mount (unless user already chose manually or auto ran)
  useEffect(() => {
    let cancelled = false;
    async function detect() {
      try {
        const alreadyManual = localStorage.getItem(LANG_KEY);
        const alreadyAuto = localStorage.getItem(AUTO_DONE_KEY);
        if (alreadyManual || alreadyAuto) return;
        const { data } = await api.get('/geo');
        if (cancelled) return;
        const suggested = data?.suggested_language === 'bn' ? 'bn' : 'en';
        setLangState(suggested);
        try {
          localStorage.setItem(LANG_KEY, suggested);
          localStorage.setItem(AUTO_DONE_KEY, '1');
        } catch { /* ignore */ }
      } catch { /* ignore — keep default */ }
    }
    detect();
    return () => { cancelled = true; };
  }, []);

  const setLang = useCallback((next) => {
    const n = next === 'bn' ? 'bn' : 'en';
    setLangState(n);
    try { localStorage.setItem(LANG_KEY, n); } catch { /* ignore */ }
  }, []);

  const t = useCallback((key) => {
    if (!key) return '';
    if (lang === 'en') return key;
    const v = TRANSLATIONS[key];
    return v || key;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

// Convenience hook — just the translator function
export function useT() {
  return useContext(I18nContext).t;
}
